import type { CaseForPharmacy } from "@omony/domain";
import { Badge, Button, EmptyState, Skeleton, UrgencyBadge } from "@omony/ui";
import { FileQuestion } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { ApiError } from "../../shared/api.js";
import { navigate } from "../../shared/hashRoute.js";
import { useCase } from "../data.js";
import { useVideo } from "../../shared/video.js";
import { BillingStep, DoneStep, ResultStep } from "./Closing.js";
import { CancelButton } from "./CancelButton.js";
import { CancelledStep, TriageOutcome } from "./Outcomes.js";
import { useCaseLabel, useT } from "./common.js";
import { ConfirmStep } from "./ConfirmStep.js";
import { FlowStepper, type StepId } from "./FlowStepper.js";
import styles from "./flow.module.css";
import { LegalPanel } from "./LegalPanel.js";
import { PatientStep } from "./PatientStep.js";
import { QueueStep, WaitingStep } from "./Queue.js";
import { TriageStep } from "./TriageStep.js";
import { VideoStep } from "./VideoStep.js";

type Screen =
  | "patient"
  | "confirm"
  | "triage"
  | "queue"
  | "waiting"
  | "video"
  | "result"
  | "billing"
  | "done"
  | "outcome"
  | "cancelled";

const STEP_OF: Record<Screen, StepId> = {
  patient: "patient",
  confirm: "bestaetigen",
  triage: "ersteinschaetzung",
  outcome: "ersteinschaetzung",
  queue: "warten",
  waiting: "warten",
  video: "video",
  result: "ergebnis",
  billing: "abrechnung",
  done: "abrechnung",
  cancelled: "abrechnung",
};

/** Bildschirm aus dem Serverzustand ableiten (A19) – lokal nur „korrigieren“ und „weiter zur Abrechnung“ */
export function screenFor(c: CaseForPharmacy, ui: { edit: boolean; toBilling: boolean }): Screen {
  switch (c.status) {
    case "angelegt":
      if (!c.patient || ui.edit) return "patient";
      if (!c.patientConfirmedAt) return "confirm";
      return "triage";
    case "triagiert":
      return "queue";
    case "wartend":
      return "waiting";
    case "angenommen":
    case "in_behandlung":
      return "video";
    case "abgeschlossen":
      if (c.billing.status === "offen") return ui.toBilling ? "billing" : "result";
      return "done";
    case "abgebrochen":
      if (c.billing.status === "eingereicht") return "done";
      if (c.cancelReason === "notfall" || c.cancelReason === "nicht_geeignet")
        return c.billing.status === "offen" && ui.toBilling ? "billing" : "outcome";
      if (c.billing.status === "offen") return "billing";
      return "cancelled";
  }
}

export function CaseFlow({ caseId }: { caseId: string }) {
  const t = useT();
  const q = useCase(caseId);
  const label = useCaseLabel();
  const video = useVideo();
  const [edit, setEdit] = useState(false);
  const [toBilling, setToBilling] = useState(false);
  const c = q.data;
  const screen = c ? screenFor(c, { edit, toBilling }) : null;

  // Video nur in den Gesprächszuständen halten; danach Iframe entfernen
  useEffect(() => {
    if (!c) return;
    if (c.status === "angenommen" || c.status === "in_behandlung") video.setCaseId(c.id);
    else if (video.caseId === c.id) video.setCaseId(null);
  }, [c, video]);

  // Beim Schrittwechsel Fokus auf die Überschrift (Screenreader, Tastatur)
  useEffect(() => {
    document.getElementById("step-title")?.focus({ preventScroll: false });
  }, [screen]);

  if (q.isLoading) return <Skeleton lines={6} />;
  if (!c) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <EmptyState
        icon={<FileQuestion />}
        title={t(notFound ? "console.case.notFound" : "error.generic.text")}
      >
        {notFound ? null : <ErrorBanner error={q.error} />}
        <Button variant="secondary" onPress={() => navigate("")}>
          {t("nav.home")}
        </Button>
      </EmptyState>
    );
  }

  let body;
  switch (screen!) {
    case "patient":
      body = <PatientStep c={c} onSaved={() => setEdit(false)} />;
      break;
    case "confirm":
      body = <ConfirmStep c={c} onEdit={() => setEdit(true)} />;
      break;
    case "triage":
      body = <TriageStep c={c} />;
      break;
    case "outcome":
      body = <TriageOutcome c={c} onBilling={() => setToBilling(true)} />;
      break;
    case "queue":
      body = <QueueStep c={c} />;
      break;
    case "waiting":
      body = <WaitingStep c={c} />;
      break;
    case "video":
      body = <VideoStep c={c} />;
      break;
    case "result":
      body = <ResultStep c={c} onNext={() => setToBilling(true)} />;
      break;
    case "billing":
      body = <BillingStep c={c} />;
      break;
    case "done":
      body = <DoneStep c={c} />;
      break;
    case "cancelled":
      body = <CancelledStep c={c} />;
      break;
  }

  return (
    <div className={styles.page}>
      <div className={styles.main}>
        <FlowStepper step={STEP_OF[screen!]} />
        <div className={styles.caseHead}>
          <div className={styles.caseMeta}>
            <strong>{label(c)}</strong>
            <span className={styles.mono}>{c.reference}</span>
            <Badge tone="accent">{t(`case.status.${c.status}`)}</Badge>
            {c.urgency ? <UrgencyBadge urgency={c.urgency} /> : null}
            {c.signageNumber !== null ? (
              <Badge>{t("console.case.number", { n: c.signageNumber })}</Badge>
            ) : null}
          </div>
          <CancelButton c={c} />
        </div>
        {body}
      </div>
      <LegalPanel step={STEP_OF[screen!]} />
    </div>
  );
}
